import sumBy from 'lodash/sumBy'
import moment from 'moment'
import React from 'react'
import { i18n } from '../../index'
import type { WastageNote, WastageReport } from './types'
import css from './wastageTable.css'
type WastageTableProps = {
  data: Array<WastageReport>
  activeStation: any
  wastageData: any
  handleSelectNote: (note: WastageNote) => void
}
type DayRowProps = {
  data: WastageReport
  wastageBoundaries: any
  handleSelectNote: (note: WastageNote) => void
}
export const calcWastagePercent = (initial, wastage) => {
  if (!initial || !wastage) {
    return 0
  }

  return (wastage / initial) * 100
}

const getWastageClass = (percentage, wastageBoundaries) => {
  const { lowerWastageBounds, upperWastageBounds } = wastageBoundaries
  if (percentage < lowerWastageBounds) return css.below
  if (percentage > upperWastageBounds) return css.above
  return css.within
}

const DayRow = ({ data, wastageBoundaries, handleSelectNote }: DayRowProps) => {
  const { date, wastageAmount, wastageVolume, salesVolume, salesAmount, note } = data
  const salesLossPercentage = calcWastagePercent(salesAmount, wastageAmount)
  const wastageClass = getWastageClass(salesLossPercentage, wastageBoundaries)
  return (
    <tr key={date} className={css.dayRow}>
      <td>{moment(date).format('D.M - dd')}</td>
      <td>{Number(salesVolume).toFixed(0)}</td>
      <td>{Number(wastageVolume).toFixed(0)}</td>
      <td>{Number(wastageAmount).toFixed(0)}</td>
      <td className={wastageClass}>{Number(salesLossPercentage).toFixed(2)} %</td>
      <td>
        <p
          onClick={() =>
            handleSelectNote({
              date,
              notes: note,
            })
          }
          className={css.notes}
        >
          {note}
        </p>
      </td>
    </tr>
  )
}

const WastageTable = ({ data, wastageData, handleSelectNote }: WastageTableProps) => {
  const getWastageBoundaries = () => {
    const { lowerWastageBounds, upperWastageBounds } = wastageData
    return {
      lowerWastageBounds,
      upperWastageBounds,
    }
  }

  const monthlyWastageVolume = sumBy(data, 'wastageVolume')
  const monthlyWastageEuros = sumBy(data, 'wastageAmount')
  const monthlySalesVolume = sumBy(data, 'salesVolume')
  const monthlySalesAmount = sumBy(data, 'salesAmount')
  const monthlyWastagePercent = calcWastagePercent(monthlySalesAmount, monthlyWastageEuros)
  const wastageClass = getWastageClass(monthlyWastagePercent, wastageData)
  return (
    <table className={css.table}>
      <thead>
        <tr className={css.tableHead}>
          <th />
          <th>{i18n.t('reporting.label.customerSales.salesVolume')}</th>
          <th>{i18n.t('reporting.wastageVolume')}</th>
          <th>{i18n.t('reporting.wastageEuros')}</th>
          <th>{i18n.t('reporting.wastagePercent')}</th>
          <th>{i18n.t('reporting.notes')}</th>
        </tr>
      </thead>
      <tbody>
        <tr className={css.monthRow}>
          <td>{i18n.t('reporting.wholeMonth')}</td>
          <td>{Number(monthlySalesVolume).toFixed(0)}</td>
          <td>{Number(monthlyWastageVolume).toFixed(0)}</td>
          <td>{Number(monthlyWastageEuros).toFixed(0)}</td>
          <td className={wastageClass}>{Number(monthlyWastagePercent).toFixed(2)} %</td>
          <td />
        </tr>
        {data.map((singleDayData, index) => {
          const weekData = data.slice(Math.max(index - 6, 0), index + 1)
          const weeklyWastageVolume = sumBy(weekData, 'wastageVolume')
          const weeklyWastageEuros = sumBy(weekData, 'wastageAmount')
          const weeklySalesVolume = sumBy(weekData, 'salesVolume')
          const weeklySalesAmount = sumBy(weekData, 'salesAmount')
          const weeklyWastagePercent = calcWastagePercent(weeklySalesAmount, weeklyWastageEuros)
          const isSunday = moment(singleDayData.date).day() === 0
          const week = moment(singleDayData.date).week()
          const wastageClass = getWastageClass(weeklyWastagePercent, wastageData)
          return (
            <React.Fragment key={singleDayData.date}>
              <DayRow
                handleSelectNote={handleSelectNote}
                data={singleDayData}
                wastageBoundaries={getWastageBoundaries()}
              />
              {isSunday && (
                <tr className={css.weekRow}>
                  <td>{`${i18n.t('common.weekShort')} ${week}`}</td>
                  <td>{Number(weeklySalesVolume).toFixed(0)}</td>
                  <td>{Number(weeklyWastageVolume).toFixed(0)}</td>
                  <td>{Number(weeklyWastageEuros).toFixed(0)}</td>
                  <td className={wastageClass}>{Number(weeklyWastagePercent).toFixed(2)} %</td>
                  <td />
                </tr>
              )}
            </React.Fragment>
          )
        })}
      </tbody>
    </table>
  )
}

export default WastageTable
